// components/Navbar.js
import React, { useState, useEffect, useRef } from "react";
import { Link, NavLink } from "react-router-dom";
import "../styles/Navbar.css";
import Logo from "../assets/ARSSLogo.png";
import { FaBars, FaTimes, FaChevronDown, FaChevronUp } from "react-icons/fa";

function Navbar({ isBannerVisible }) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [aboutOpen, setAboutOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const menuRef = useRef();
  const navbarRef = useRef();
  const [isMobile, setIsMobile] = useState(false);

  // Handle screen resize and set mobile state
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
      if (window.innerWidth > 768) {
        setMobileMenuOpen(false);
        document.body.style.overflow = '';
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Handle scroll to change navbar appearance
  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      if (scrollPosition > 50) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Toggle mobile menu
  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
    // Prevent body scroll when menu is open on mobile
    if (!mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
      setAboutOpen(false);
      setServicesOpen(false);
    }
  };

  // Toggle dropdown menus on mobile
  const toggleServicesDropdown = (e) => {
    if (isMobile) {
      e.stopPropagation();
      setServicesOpen(!servicesOpen);
      setAboutOpen(false);
    }
  };

  const toggleAboutDropdown = (e) => {
    if (isMobile) {
      e.stopPropagation();
      setAboutOpen(!aboutOpen);
      setServicesOpen(false);
    }
  };

  // Handle click outside to close menus
  useEffect(() => {
    function handleClickOutside(event) {
      // Only run this on mobile or when menu is open
      if (!isMobile && !mobileMenuOpen) return;
      
      // Close main menu if clicked outside
      if (
        menuRef.current &&
        navbarRef.current &&
        !menuRef.current.contains(event.target) &&
        !navbarRef.current.contains(event.target)
      ) {
        setMobileMenuOpen(false);
        document.body.style.overflow = '';
      }
    }
    
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isMobile, mobileMenuOpen]);

  // Close menu when route changes
  const closeAllMenus = () => {
    setMobileMenuOpen(false);
    setServicesOpen(false);
    setAboutOpen(false);
    document.body.style.overflow = '';
  };

  return (
    <nav 
      className={`navbar ${isBannerVisible ? "with-banner" : "without-banner"} ${scrolled ? "scrolled" : ""}`} 
      ref={navbarRef}
    >
      <div className="nav-container">
        <div className="nav-brand">
          <Link to="/" onClick={closeAllMenus}>
            <img src={Logo} alt="Affordable Roofing + Solar Solutions Logo" className="nav-logo" />
          </Link>
        </div>
        
        {/* Mobile menu toggle button */}
        <div className="mobile-menu-icon" onClick={toggleMobileMenu}>
          {mobileMenuOpen ? <FaTimes /> : <FaBars />}
        </div>
        
        {/* Navigation links */}
        <div className={`nav-menu ${mobileMenuOpen ? "open" : ""}`} ref={menuRef}>
          <ul className="nav-links">
            <li className="nav-item">
              <NavLink 
                exact="true"
                to="/" 
                className={({ isActive }) => isActive ? "active" : ""}
                onClick={closeAllMenus}
              >
                Home
              </NavLink>
            </li>
            
            {/* About Us dropdown */}
            <li className="nav-item dropdown-item">
              <div 
                className={`dropdown-toggle ${aboutOpen ? "active" : ""}`}
                onClick={toggleAboutDropdown}
                onMouseEnter={() => !isMobile && setAboutOpen(true)}
                onMouseLeave={() => !isMobile && setAboutOpen(false)}
              >
                <span>About Us</span>
                <span className="dropdown-icon">
                  {aboutOpen ? <FaChevronUp /> : <FaChevronDown />}
                </span>
              </div>
              
              <ul 
                className={`dropdown-menu ${aboutOpen ? "open" : ""}`}
                onMouseEnter={() => !isMobile && setAboutOpen(true)}
                onMouseLeave={() => !isMobile && setAboutOpen(false)}
              >
                <li>
                  <NavLink 
                    to="/about-us" 
                    className={({ isActive }) => isActive ? "active" : ""}
                    onClick={closeAllMenus}
                  >
                    Our Company
                  </NavLink>
                </li>
                <li>
                  <NavLink 
                    to="/affiliate" 
                    className={({ isActive }) => isActive ? "active" : ""}
                    onClick={closeAllMenus}
                  >
                    The Elevate Program
                  </NavLink>
                </li>
              </ul>
            </li>
            
            {/* Services dropdown */}
            <li className="nav-item dropdown-item">
              <div 
                className={`dropdown-toggle ${servicesOpen ? "active" : ""}`}
                onClick={toggleServicesDropdown}
                onMouseEnter={() => !isMobile && setServicesOpen(true)}
                onMouseLeave={() => !isMobile && setServicesOpen(false)}
              >
                <span>Services</span>
                <span className="dropdown-icon">
                  {servicesOpen ? <FaChevronUp /> : <FaChevronDown />}
                </span>
              </div>
              
              <ul 
                className={`dropdown-menu ${servicesOpen ? "open" : ""}`}
                onMouseEnter={() => !isMobile && setServicesOpen(true)}
                onMouseLeave={() => !isMobile && setServicesOpen(false)}
              >
                <li>
                  <NavLink 
                    to="/services/residential-services" 
                    className={({ isActive }) => isActive ? "active" : ""}
                    onClick={closeAllMenus}
                  >
                    Residential Services
                  </NavLink>
                </li>
                <li>
                  <NavLink 
                    to="/services/commercial-services" 
                    className={({ isActive }) => isActive ? "active" : ""}
                    onClick={closeAllMenus}
                  >
                    Commercial Services
                  </NavLink>
                </li>
                <li>
                  <NavLink 
                    to="/services/solar" 
                    className={({ isActive }) => isActive ? "active" : ""}
                    onClick={closeAllMenus}
                  >
                    Solar
                  </NavLink>
                </li>
              </ul>
            </li>
            
            {/* Contact button for mobile */}
            <li className="nav-item mobile-only">
              <NavLink
                to="/contact"
                className="contact-button"
                onClick={closeAllMenus}
              >
                Contact Us
              </NavLink>
            </li>

            {/* Request Inspection button for mobile */}
            <li className="nav-item mobile-only">
              <NavLink
                to="/signup"
                className="inspection-button"
                onClick={closeAllMenus}
              >
                Request Inspection
              </NavLink>
            </li>
          </ul>

          {/* Contact + Inspection buttons for desktop */}
          <div className="desktop-only">
            <NavLink
              to="/contact"
              className="contact-button"
              onClick={closeAllMenus}
            >
              Contact Us
            </NavLink>
            <NavLink
              to="/signup"
              className="inspection-button"
              onClick={closeAllMenus}
            >
              Request Inspection
            </NavLink>
          </div>
        </div>
      </div>
    </nav>
  );
}

export default Navbar;